import { z } from 'zod';
import { EnrollmentType } from 'libs/end-to-end/src/types/users/user';
import { OrganizationUserRole } from 'libs/end-to-end/src/types/users/organization-user-roles';
import { EntitlementFeatureKey } from 'libs/end-to-end/src/group-entitlements';
import { FocusAreaEnum } from 'libs/end-to-end/src/focus-area';
import { DataRule } from 'libs/end-to-end/src/data-rules';
import { CustomVizRequest } from 'libs/end-to-end/src/custom-viz/CustomVizRequest';
import { DatabaseFeatureFlagKey } from 'libs/end-to-end/src/database-feature-flags';

export const AuditOps = ['create', 'update', 'delete'] as const;
export const AuditOp = z.enum(AuditOps);
export type AuditOp = z.infer<typeof AuditOp>;

const BaseAuditDto = z.object({
  id: z.string().uuid(),
  sourceId: z.string().uuid(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  auditedAt: z.string(),
  auditOp: AuditOp,
  auditSessionUser: z.string(),
  auditActorRole: z.string().nullable(),
  auditActorUserId: z.string().nullable(),
});

export const UserAuditDto = BaseAuditDto.extend({
  cognitoUsername: z.string(),
  email: z.string(),
  isSuperAdmin: z.boolean(),
  passwordResetToken: z.string().nullable(),
  passwordResetTokenExpiresAt: z.coerce.date().nullable(),
  enrollmentType: EnrollmentType,
});

export const OrganizationAuditDto = BaseAuditDto.extend({
  name: z.string(),
  logoMimeType: z.string().nullable(),
  logoUrl: z.string().nullable(),
});

export const OrganizationUserAuditDto = BaseAuditDto.extend({
  userId: z.string().uuid(),
  organizationId: z.string().uuid(),
});

export const OrganizationUserRoleAuditDto = BaseAuditDto.extend({
  organizationUserId: z.string().uuid(),
  role: OrganizationUserRole,
});

export const OrganizationGroupAuditDto = BaseAuditDto.extend({
  organizationId: z.string().uuid(),
  name: z.string(),
  description: z.string().nullable(),
});

export const OrganizationGroupUserAuditDto = BaseAuditDto.extend({
  organizationGroupId: z.string().uuid(),
  organizationUserId: z.string().uuid(),
});

export const OrganizationGroupEntitlementAuditDto = BaseAuditDto.extend({
  organizationGroupId: z.string().uuid(),
  databaseId: z.string().uuid(),
});

export const OrganizationGroupEntitlementFeatureAuditDto = BaseAuditDto.extend({
  organizationGroupEntitlementId: z.string().uuid(),
  key: EntitlementFeatureKey,
  focusArea: FocusAreaEnum.nullable(),
});

export const OrganizationGroupEntitlementDataRuleAuditDto = BaseAuditDto.extend(
  {
    organizationGroupEntitlementId: z.string().uuid(),
    description: z.string().nullable(),
    rule: DataRule,
  },
);

export const DashboardAuditDto = BaseAuditDto.extend({
  name: z.string(),
  ownerUserId: z.string().uuid(),
  organizationId: z.string().uuid(),
  databaseId: z.string().uuid(),
});

export const DashboardChartAuditDto = BaseAuditDto.extend({
  dashboardId: z.string().uuid(),
  customVizRequest: CustomVizRequest,
  name: z.string(),
  description: z.string().nullable(),
  positionOnDashboard: z.number(),
  positionOnRow: z.number(),
  width: z.number(),
  height: z.number().nullable(),
});

export const DashboardExternalUserAuditDto = BaseAuditDto.extend({
  dashboardId: z.string().uuid(),
  email: z.string(),
  state: z.union([z.literal('active'), z.literal('revoked')]),
  invitedByUserId: z.string().uuid(),
  lastInvitationSentAt: z.coerce.date().nullable(),
  revokedAt: z.coerce.date().nullable(),
  revokedByUserId: z.string().uuid().nullable(),
});

export const OrganizationPrivateNetworkingEnablementAuditDto =
  BaseAuditDto.extend({
    organizationId: z.string().uuid(),
    enabled: z.boolean(),
    whitelistedHosts: z.array(z.string()),
  });

export const DatabaseFeatureFlagsAuditDto = BaseAuditDto.extend({
  databaseId: z.string().uuid(),
  key: DatabaseFeatureFlagKey,
  enabled: z.boolean(),
});

export const AuditDto = z.discriminatedUnion('auditTable', [
  UserAuditDto.extend({ auditTable: z.literal('users') }),
  OrganizationAuditDto.extend({ auditTable: z.literal('organizations') }),
  OrganizationUserAuditDto.extend({
    auditTable: z.literal('organizationUsers'),
  }),
  OrganizationUserRoleAuditDto.extend({
    auditTable: z.literal('organizationUserRoles'),
  }),
  OrganizationGroupAuditDto.extend({
    auditTable: z.literal('organizationGroups'),
  }),
  OrganizationGroupUserAuditDto.extend({
    auditTable: z.literal('organizationGroupUsers'),
  }),
  OrganizationGroupEntitlementAuditDto.extend({
    auditTable: z.literal('organizationGroupEntitlements'),
  }),
  OrganizationGroupEntitlementFeatureAuditDto.extend({
    auditTable: z.literal('organizationGroupEntitlementFeatures'),
  }),
  OrganizationGroupEntitlementDataRuleAuditDto.extend({
    auditTable: z.literal('organizationGroupEntitlementDataRules'),
  }),
  DashboardAuditDto.extend({ auditTable: z.literal('dashboards') }),
  DashboardChartAuditDto.extend({ auditTable: z.literal('dashboardCharts') }),
  DashboardExternalUserAuditDto.extend({
    auditTable: z.literal('dashboardExternalUsers'),
  }),
  OrganizationPrivateNetworkingEnablementAuditDto.extend({
    auditTable: z.literal('organizationPrivateNetworkingEnablement'),
  }),
  DatabaseFeatureFlagsAuditDto.extend({
    auditTable: z.literal('databaseFeatureFlags'),
  }),
]);

export type AuditDto = z.infer<typeof AuditDto>;

export const AuditTableQueryParam = z.union([
  z.literal('users'),
  z.literal('mfaCodes'),
  z.literal('organizations'),
  z.literal('organizationUsers'),
  z.literal('organizationUserRoles'),
  z.literal('organizationGroups'),
  z.literal('organizationGroupUsers'),
  z.literal('organizationGroupEntitlements'),
  z.literal('organizationGroupEntitlementFeatures'),
  z.literal('organizationGroupEntitlementDataRules'),
  z.literal('dashboards'),
  z.literal('dashboardCharts'),
  z.literal('dashboardExternalUsers'),
  z.literal('organizationPrivateNetworkingEnablement'),
  z.literal('databaseFeatureFlags'),
]);

export type AuditTableQueryParam = z.infer<typeof AuditTableQueryParam>;

const timestampRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{1,6})?Z$/;

export const GetAuditsQueryDto = z.object({
  auditTable: AuditTableQueryParam,
  sourceId: z.string().uuid().optional(),
  actorUserId: z.string().uuid().optional(),
  pageSize: z.coerce.number().min(1).max(10000).optional(),
  cursor: z.string().optional(),
  dateRange: z
    .object({
      from: z.string().regex(timestampRegex).optional(),
      to: z.string().regex(timestampRegex).optional(),
    })
    .optional(),
});

export type GetAuditsQueryDto = z.infer<typeof GetAuditsQueryDto>;

export const GetAuditsResponseDto = z.object({
  audits: z.array(AuditDto),
  nextCursor: z.string().nullable(),
});

export type GetAuditsResponseDto = z.infer<typeof GetAuditsResponseDto>;
